import React from 'react';
import { ArrowMoveDownCustomIcon } from '@assets/icon';
import type { FeatureSuggestItem } from '@services/chatService';
import './AtFeatureSuggestions.scss';

interface AtFeatureSuggestionsProps {
  items: FeatureSuggestItem[];
  isLoading: boolean;
  onSelect: (item: FeatureSuggestItem) => void;
}

const SuggestionIcon: React.FC<{ item: FeatureSuggestItem }> = ({ item }) => {
  const [hasError, setHasError] = React.useState(false);
  if (!item.imageUrl || hasError) {
    return <span className="at-feature-suggestions__fallback" aria-hidden>@</span>;
  }
  return (
    <img
      className="at-feature-suggestions__icon"
      src={item.imageUrl}
      alt=""
      onError={() => setHasError(true)}
    />
  );
};

const LoadingSuggestions = () => (
  <div
    className="at-feature-suggestions at-feature-suggestions--loading"
    role="status"
    aria-label="Đang tải gợi ý tính năng"
    aria-busy="true"
  >
    <div className="at-feature-suggestions__viewport">
      {[0, 1, 2].map((index) => (
        <span key={index} className="at-feature-suggestions__skeleton" />
      ))}
    </div>
  </div>
);

const SuggestionItem: React.FC<{
  item: FeatureSuggestItem;
  onSelect: (item: FeatureSuggestItem) => void;
}> = ({ item, onSelect }) => (
  <button type="button" role="option" aria-selected="false"
    className="at-feature-suggestions__item"
    onMouseDown={(event) => event.preventDefault()}
    onClick={() => onSelect(item)}>
    <SuggestionIcon item={item} />
    <span className="at-feature-suggestions__title">{item.featureName}</span>
    <span className="at-feature-suggestions__arrow" aria-hidden>
      <ArrowMoveDownCustomIcon />
    </span>
  </button>
);

const SuggestionList: React.FC<Omit<AtFeatureSuggestionsProps, 'isLoading'>> = ({ items, onSelect }) => (
  <div
    className={`at-feature-suggestions${items.length > 5 ? ' at-feature-suggestions--scrollable' : ''}`}
  >
    <div className="at-feature-suggestions__viewport" role="listbox"
      aria-label="Gợi ý tính năng Tendoo">
      {items.map((item) => (
        <SuggestionItem key={item.featureCode} item={item} onSelect={onSelect} />
      ))}
    </div>
  </div>
);

const AtFeatureSuggestions: React.FC<AtFeatureSuggestionsProps> = ({
  items,
  isLoading,
  onSelect,
}) => {
  if (isLoading && items.length === 0) return <LoadingSuggestions />;
  if (items.length === 0) return null;
  return <SuggestionList items={items} onSelect={onSelect} />;
};

export default AtFeatureSuggestions;
